/* ─────────────────────────────────────────────────────────────────
   Sticky product details — dynamic top offset.

   When the form fits within the available viewport (under the sticky
   header on the PDP, or inside the modal content on quick-add), the
   form pins to the top normally.

   When the form is TALLER than the available viewport, drop the
   sticky top so the BOTTOM of the form lands at the viewport bottom
   when it first pins. As the user scrolls down, the form scrolls
   along with the page until its bottom hits the viewport bottom,
   then pins and progressively reveals from bottom to top.

   Recomputes on:
     - page load
     - window / modal resize
     - any size change of the form (accordions, variant changes, etc.)
     - any change to the --sticky-header-offset value
     - new product-details inserted (e.g., quick-add modal load)
   ───────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  // PERF-07 PDP-05: the product page loads this twice (the product details block and the
  // quick-add modal each have a tag), and two runs doubled every observer and listener.
  // One run already follows product details that arrive later.
  if (window.__stickyProductDetails) return;
  window.__stickyProductDetails = true;

  const MOBILE_MAX = 749;
  const isMobile = () =>
    window.matchMedia(`(max-width: ${MOBILE_MAX}px)`).matches;

  function parseLength(raw) {
    if (!raw) return 0;
    raw = String(raw).trim();
    if (!raw) return 0;
    if (raw.endsWith('px')) return parseFloat(raw) || 0;
    if (raw.endsWith('rem')) {
      const rem =
        parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      return (parseFloat(raw) || 0) * rem;
    }
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 0;
  }

  function getHeaderOffset(el) {
    const cs = getComputedStyle(el);
    return parseLength(cs.getPropertyValue('--sticky-header-offset'));
  }

  /** Return the modal-content scroll container if the form is inside one,
   *  else null. */
  function findModalContent(el) {
    return el.closest('.quick-add-modal__content');
  }

  function computeStickyTop(el) {
    if (isMobile()) {
      el.style.removeProperty('--sticky-top');
      return;
    }

    const modalContent = findModalContent(el);
    const formHeight = el.offsetHeight;

    let viewport;
    let headerOffset;

    if (modalContent) {
      // Inside the quick-add modal — use the modal's own scroll viewport.
      viewport = modalContent.clientHeight;
      headerOffset = 0;
    } else {
      // PDP — use the window viewport + sticky header offset.
      viewport = window.innerHeight;
      headerOffset = getHeaderOffset(el);
    }

    const available = viewport - headerOffset;

    if (formHeight <= available) {
      // Form fits — pin top under header (or to 0 in modal).
      el.style.setProperty('--sticky-top', `${headerOffset}px`);
    } else {
      // Form taller than viewport — drop sticky-top so the form's bottom
      // aligns with the bottom of the viewport on first pin.
      const top = viewport - formHeight;
      el.style.setProperty('--sticky-top', `${top}px`);
    }
  }

  function init(el) {
    if (!el || el.dataset.stickyTopBound === '1') return;
    el.dataset.stickyTopBound = '1';

    const recompute = () => computeStickyTop(el);
    recompute();

    // Recompute on form size changes (accordions, variant swap, etc.)
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(recompute);
      ro.observe(el);

      // Also observe the modal scroll container if present, since its
      // height changes when the modal animates open/closed.
      const modalContent = findModalContent(el);
      if (modalContent) ro.observe(modalContent);
    }

    // Window resize covers viewport changes for both modes.
    window.addEventListener('resize', recompute, { passive: true });

    // PDP: recompute when the page header sticky state flips.
    const header = document.getElementById('header-component');
    if (header && 'MutationObserver' in window) {
      const mo = new MutationObserver(recompute);
      mo.observe(header, {
        attributes: true,
        attributeFilter: ['data-sticky-state'],
      });
    }
  }

  function shouldBind(el) {
    // PDP: only when the merchant has enabled the sticky setting.
    // Modal: always (the modal forces sticky for the form on desktop).
    return (
      el.classList.contains('sticky-content--desktop') ||
      el.closest('.quick-add-modal__content') !== null
    );
  }

  function boot() {
    document.querySelectorAll('.product-details').forEach((el) => {
      if (shouldBind(el)) init(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Watch for product-details inserted later (quick-add modal content
  // is fetched & injected after click).
  if ('MutationObserver' in window) {
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (
            node.matches &&
            node.matches('.product-details') &&
            shouldBind(node)
          ) {
            init(node);
          } else if (node.querySelectorAll) {
            node.querySelectorAll('.product-details').forEach((el) => {
              if (shouldBind(el)) init(el);
            });
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
